/**
 * Campaign & Dialer Dashboard
 * 
 * Modern unified campaign management and dialer monitoring
 * Designed to match SupervisorDashboard styling
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tabs,
    Tab,
    Tooltip,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    LinearProgress,
    Switch,
    FormControlLabel,
    Slider,
    Collapse,
    Skeleton,
    Drawer,
    Divider,
    keyframes
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
    IconSpeakerphone,
    IconPlus,
    IconEdit,
    IconTrash,
    IconPlayerPlay,
    IconPlayerPause,
    IconPlayerStop,
    IconRefresh,
    IconCopy,
    IconUsers,
    IconPhone,
    IconClock,
    IconUpload,
    IconTrendingUp,
    IconPhoneCall,
    IconPower,
    IconX,
    IconAlertCircle,
    IconChevronDown,
    IconChevronUp,
    IconTarget,
    IconDatabase,
    IconArrowRight,
    IconFileSpreadsheet,
    IconCalendar,
    IconDownload,
    IconRotateClockwise,
} from '@tabler/icons-react';
import campaignService, { Campaign, CampaignStatus } from '../services/campaignService';
import dialerListService, { DialerList } from '../services/dialerListService';
import { queueAPI } from '../services/api';
import io from 'socket.io-client';
import { SOCKET_URL } from '../config';

interface Queue {
    _id: string;
    name: string;
    assignedAgentIds?: Array<{ _id: string; name: string }>;
}

interface ServiceStatus {
    isRunning: boolean;
    activeCampaigns: number;
    activeDialerCalls: number;
    amiConnected: boolean;
}

// Animations matching SupervisorDashboard
const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
`;

// Status configurations
const statusConfig: Record<string, { color: string; bgColor: string; icon: React.ReactNode; label: string }> = {
    draft: { color: '#64748b', bgColor: '#f1f5f9', icon: <IconEdit size={16} />, label: 'Taslak' },
    scheduled: { color: '#0ea5e9', bgColor: '#e0f2fe', icon: <IconClock size={16} />, label: 'Zamanlanmış' },
    running: { color: '#22c55e', bgColor: '#dcfce7', icon: <IconPlayerPlay size={16} />, label: 'Çalışıyor' },
    active: { color: '#22c55e', bgColor: '#dcfce7', icon: <IconPlayerPlay size={16} />, label: 'Aktif' },
    paused: { color: '#f59e0b', bgColor: '#fef3c7', icon: <IconPlayerPause size={16} />, label: 'Duraklatıldı' },
    completed: { color: '#8b5cf6', bgColor: '#ede9fe', icon: <IconTarget size={16} />, label: 'Tamamlandı' },
    cancelled: { color: '#ef4444', bgColor: '#fee2e2', icon: <IconX size={16} />, label: 'İptal' },
    archived: { color: '#6b7280', bgColor: '#f3f4f6', icon: <IconTrash size={16} />, label: 'Arşivlendi' }
};

const dialerTypes: Record<string, { label: string; description: string }> = {
    predictive: { label: 'Predictive', description: 'Otomatik tahminli arama' },
    power: { label: 'Power', description: 'Güçlü sıralı arama' },
    progressive: { label: 'Progressive', description: 'Kademeli arama' },
    preview: { label: 'Preview', description: 'Önizlemeli arama' }
};

export default function CampaignDashboard() {
    // Data state
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [queues, setQueues] = useState<Queue[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [serviceStatus, setServiceStatus] = useState<ServiceStatus | null>(null);
    
    // UI state
    const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
    const [expandedCampaigns, setExpandedCampaigns] = useState<Set<string>>(new Set());
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDrawerOpen, setEditDrawerOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [restartConfirmOpen, setRestartConfirmOpen] = useState(false);
    const [csvDialogOpen, setCsvDialogOpen] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    
    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        type: 'predictive' as Campaign['type'],
        queueId: '',
        callerIdNumber: '',
        callerIdName: '',
        dialerSettings: {
            maxConcurrentCalls: 10,
            targetAgentWaitTime: 5,
            maxDialRatio: 3.0,
            minDialRatio: 1.0,
            maxAbandonRate: 0.03,
            ringTimeout: 25,
            retryAttempts: 3,
            retryDelayMinutes: 30
        },
        workingHours: {
            enabled: true,
            timezone: 'Europe/Istanbul',
            days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
            timeSlots: [{ startTime: '09:00', endTime: '18:00' }]
        }
    });
    
    // CSV state
    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [csvListName, setCsvListName] = useState('');
    const [csvUploading, setCsvUploading] = useState(false);
    
    // Data Pool state
    const [mainTab, setMainTab] = useState(0); // 0: Dialerlar, 1: Veri Havuzu
    const [poolLists, setPoolLists] = useState<DialerList[]>([]);
    const [poolLoading, setPoolLoading] = useState(false);
    const [poolDialogOpen, setPoolDialogOpen] = useState(false);
    const [poolCsvFile, setPoolCsvFile] = useState<File | null>(null);
    const [poolListName, setPoolListName] = useState('');
    const [poolUploading, setPoolUploading] = useState(false);
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [assignCampaignId, setAssignCampaignId] = useState('');
    const [assignListId, setAssignListId] = useState('');
    const [assigning, setAssigning] = useState(false);
    
    // Socket
    const [socket, setSocket] = useState<any>(null);
    
    useEffect(() => {
        const newSocket = io(SOCKET_URL);
        setSocket(newSocket);
        return () => { newSocket.disconnect(); };
    }, []);
    
    useEffect(() => {
        if (!socket) return;
        const handleDialerEvent = (event: any) => {
            if (['campaign-started', 'campaign-stopped', 'campaign-completed', 'campaign-paused'].includes(event.event)) {
                loadData();
            }
        };
        socket.on('dialer-event', handleDialerEvent);
        return () => { socket.off('dialer-event', handleDialerEvent); };
    }, [socket]);
    
    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const [campaignsData, queuesRes, statusData] = await Promise.all([
                campaignService.getCampaigns(),
                queueAPI.getAll(),
                campaignService.getActiveCampaignsStatus().catch(() => null)
            ]);
            setCampaigns(campaignsData);
            const qData = queuesRes?.data?.queues || queuesRes?.data?.data || queuesRes?.data || [];
            setQueues(Array.isArray(qData) ? qData : []);
            if (statusData) setServiceStatus(statusData.serviceStatus);
            setError(null);
        } catch (err: any) {
            setError(err.message || 'Veriler yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    }, []);
    
    useEffect(() => {
        loadData();
        const interval = setInterval(loadData, 10000);
        return () => clearInterval(interval);
    }, [loadData]);
    
    // Campaign actions
    const handleStart = async (campaign: Campaign) => {
        try { await campaignService.startCampaign(campaign._id); loadData(); } catch (err: any) { setError(err.message); }
    };
    const handlePause = async (campaign: Campaign) => {
        try { await campaignService.pauseCampaign(campaign._id); loadData(); } catch (err: any) { setError(err.message); }
    };
    const handleResume = async (campaign: Campaign) => {
        try { await campaignService.resumeCampaign(campaign._id); loadData(); } catch (err: any) { setError(err.message); }
    };
    const handleStop = async (campaign: Campaign) => {
        try { await campaignService.stopCampaign(campaign._id); loadData(); } catch (err: any) { setError(err.message); }
    };
    const handleRestart = async () => {
        if (!selectedCampaign) return;
        try { 
            await campaignService.restartCampaign(selectedCampaign._id); 
            setRestartConfirmOpen(false); 
            setSelectedCampaign(null); 
            loadData(); 
        } catch (err: any) { setError(err.message); }
    };
    const handleDelete = async () => {
        if (!selectedCampaign) return;
        try { await campaignService.deleteCampaign(selectedCampaign._id); setDeleteConfirmOpen(false); setSelectedCampaign(null); loadData(); } catch (err: any) { setError(err.message); }
    };
    const handleClone = async (campaign: Campaign) => {
        try { await campaignService.cloneCampaign(campaign._id, campaign.name + ' (Kopya)'); loadData(); } catch (err: any) { setError(err.message); }
    };
    const handleCreate = async () => {
        try { await campaignService.createCampaign(formData as any); setCreateDialogOpen(false); resetForm(); loadData(); } catch (err: any) { setError(err.message); }
    };
    const handleUpdate = async () => {
        if (!selectedCampaign) return;
        try { await campaignService.updateCampaign(selectedCampaign._id, formData as any); setEditDrawerOpen(false); loadData(); } catch (err: any) { setError(err.message); }
    };
    
    const handleServiceToggle = async () => {
        try {
            if (serviceStatus?.isRunning) { await campaignService.stopDialerService(); }
            else { await campaignService.startDialerService(); }
            loadData();
        } catch (err: any) { setError(err.message); }
    };
    
    const resetForm = () => {
        setFormData({
            name: '', description: '', type: 'predictive', queueId: '', callerIdNumber: '', callerIdName: '',
            dialerSettings: { maxConcurrentCalls: 10, targetAgentWaitTime: 5, maxDialRatio: 3.0, minDialRatio: 1.0, maxAbandonRate: 0.03, ringTimeout: 25, retryAttempts: 3, retryDelayMinutes: 30 },
            workingHours: { enabled: true, timezone: 'Europe/Istanbul', days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'], timeSlots: [{ startTime: '09:00', endTime: '18:00' }] }
        });
    };
    
    const openEditDrawer = (campaign: Campaign) => {
        setSelectedCampaign(campaign);
        setFormData({
            name: campaign.name, description: campaign.description || '', type: campaign.type,
            queueId: campaign.queueId?._id || '', callerIdNumber: campaign.callerIdNumber || '', callerIdName: campaign.callerIdName || '',
            dialerSettings: campaign.dialerSettings || formData.dialerSettings,
            workingHours: campaign.workingHours || formData.workingHours
        });
        setEditDrawerOpen(true);
    };
    
    const toggleExpand = (campaignId: string) => {
        setExpandedCampaigns(prev => {
            const next = new Set(prev);
            if (next.has(campaignId)) next.delete(campaignId); else next.add(campaignId);
            return next;
        });
    };
    
    const handleCsvUpload = async () => {
        if (!csvFile || !selectedCampaign) return;
        setCsvUploading(true);
        try {
            const newList = await dialerListService.createList({
                name: csvListName || csvFile.name.replace('.csv', ''),
                campaignId: selectedCampaign._id,
                description: `CSV'den içe aktarıldı: ${csvFile.name}`
            });
            await dialerListService.importCsv(newList._id, csvFile);
            setCsvDialogOpen(false); setCsvFile(null); setCsvListName(''); loadData();
        } catch (err: any) { setError(err.message); }
        finally { setCsvUploading(false); }
    };
    
    // Data Pool functions
    const loadPoolLists = useCallback(async () => {
        try {
            setPoolLoading(true);
            const lists = await dialerListService.getPoolLists();
            setPoolLists(lists);
        } catch (err: any) {
            setError(err.message || 'Havuz verileri yüklenirken hata oluştu');
        } finally {
            setPoolLoading(false);
        }
    }, []);
    
    useEffect(() => {
        if (mainTab === 1) {
            loadPoolLists();
        }
    }, [mainTab, loadPoolLists]);
    
    const handlePoolCsvUpload = async () => {
        if (!poolCsvFile) return;
        setPoolUploading(true);
        try {
            const newList = await dialerListService.createList({
                name: poolListName || poolCsvFile.name.replace('.csv', ''),
                description: `Havuz verisi - CSV: ${poolCsvFile.name}`
            });
            await dialerListService.importCsv(newList._id, poolCsvFile);
            setPoolDialogOpen(false);
            setPoolCsvFile(null);
            setPoolListName('');
            loadPoolLists();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setPoolUploading(false);
        }
    };
    
    const handleAssignToCampaign = async () => {
        if (!assignListId || !assignCampaignId) return;
        setAssigning(true);
        try {
            const result = await dialerListService.assignToCampaign(assignListId, assignCampaignId);
            setAssignDialogOpen(false);
            setAssignCampaignId('');
            setAssignListId('');
            loadPoolLists();
            loadData();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setAssigning(false);
        }
    };
    
    const handleDeletePoolList = async (listId: string) => {
        try {
            await dialerListService.deleteList(listId);
            loadPoolLists();
        } catch (err: any) {
            setError(err.message);
        }
    };
    
    const handleExportPoolList = async (listId: string, listName: string) => {
        try {
            const blob = await dialerListService.exportList(listId);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const safeName = listName.replace(/[^a-zA-Z0-9_\-\u00C0-\u024F\u0400-\u04FF\u0600-\u06FF]/g, '_');
            link.download = `${safeName}_${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            setError(err.message || 'Rapor indirilemedi');
        }
    };
    
    // Stats - use correct field names from Campaign model
    const totalStats = campaigns.reduce((acc, c) => ({
        totalContacts: acc.totalContacts + (c.statistics?.totalContacts || 0),
        contactsDialed: acc.contactsDialed + (c.statistics?.contactsDialed || 0),
        answeredCalls: acc.answeredCalls + (c.statistics?.answeredCalls || 0),
        totalCalls: acc.totalCalls + (c.statistics?.totalCalls || 0)
    }), { totalContacts: 0, contactsDialed: 0, answeredCalls: 0, totalCalls: 0 });
    
    const runningCampaigns = campaigns.filter(c => c.status === 'running').length;
    const pausedCampaigns = campaigns.filter(c => c.status === 'paused').length;

    // ─── Stat Card (SupervisorDashboard style) ───
    const StatCard = ({ title, value, subtitle, icon, gradient }: { 
        title: string; value: string | number; subtitle?: string; icon: React.ReactNode; gradient: string 
    }) => (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)', border: '1px solid rgba(0, 0, 0, 0.05)', height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mb: 1, fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '0.8rem' }}>
                            {title}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#152b69', fontFamily: 'Arial, Helvetica, sans-serif', mb: 0.5 }}>
                            {value}
                        </Typography>
                        {subtitle && (
                            <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'Arial, Helvetica, sans-serif' }}>
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{
                        width: 48, height: 48, borderRadius: 2.5, background: gradient,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)', flexShrink: 0,
                    }}>
                        {icon}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
    
    // ─── Campaign Card ───
    const CampaignCard = ({ campaign }: { campaign: Campaign }) => {
        const status = statusConfig[campaign.status] || statusConfig.draft;
        const isExpanded = expandedCampaigns.has(campaign._id);
        const progress = campaign.statistics?.totalContacts 
            ? Math.round(((campaign.statistics.contactsDialed || 0) / campaign.statistics.totalContacts) * 100) : 0;
        
        return (
            <Card sx={{
                borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                border: campaign.status === 'running' ? '2px solid rgba(34, 197, 94, 0.4)' : '1px solid rgba(0, 0, 0, 0.06)',
                overflow: 'hidden', transition: 'all 0.3s ease',
                '&:hover': { boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)', transform: 'translateY(-2px)' }
            }}>
                {campaign.status === 'running' && (
                    <Box sx={{ height: 3, background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)', animation: `${pulse} 2s ease-in-out infinite` }} />
                )}
                
                <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#152b69', fontFamily: 'Arial', fontSize: '1rem' }}>
                                    {campaign.name}
                                </Typography>
                                <Chip size="small"
                                    icon={<Box sx={{ display: 'flex', ml: 0.5 }}>{status.icon}</Box>}
                                    label={status.label}
                                    sx={{ bgcolor: status.bgColor, color: status.color, fontWeight: 600, fontSize: '0.7rem', height: 26, fontFamily: 'Arial', '& .MuiChip-icon': { color: status.color } }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Chip size="small" variant="outlined"
                                    label={dialerTypes[campaign.type]?.label || campaign.type}
                                    sx={{ height: 22, fontSize: '0.7rem', borderColor: '#cbd5e1', color: '#475569', fontFamily: 'Arial' }}
                                />
                                {campaign.queueId && (
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5, fontFamily: 'Arial' }}>
                                        <IconUsers size={14} /> {campaign.queueId.name}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {campaign.status === 'draft' && (
                                <Tooltip title="Başlat">
                                    <IconButton size="small" onClick={() => handleStart(campaign)} sx={{ color: '#22c55e', '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.1)' } }}>
                                        <IconPlayerPlay size={18} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {campaign.status === 'running' && (<>
                                <Tooltip title="Duraklat">
                                    <IconButton size="small" onClick={() => handlePause(campaign)} sx={{ color: '#f59e0b', '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.1)' } }}>
                                        <IconPlayerPause size={18} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Durdur">
                                    <IconButton size="small" onClick={() => handleStop(campaign)} sx={{ color: '#ef4444', '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' } }}>
                                        <IconPlayerStop size={18} />
                                    </IconButton>
                                </Tooltip>
                            </>)}
                            {campaign.status === 'paused' && (<>
                                <Tooltip title="Devam Et">
                                    <IconButton size="small" onClick={() => handleResume(campaign)} sx={{ color: '#22c55e', '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.1)' } }}>
                                        <IconPlayerPlay size={18} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Durdur">
                                    <IconButton size="small" onClick={() => handleStop(campaign)} sx={{ color: '#ef4444', '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' } }}>
                                        <IconPlayerStop size={18} />
                                    </IconButton>
                                </Tooltip>
                            </>)}
                            {(campaign.status === 'completed' || campaign.status === 'cancelled') && (
                                <Tooltip title="Baştan Başlat">
                                    <IconButton size="small" onClick={() => { setSelectedCampaign(campaign); setRestartConfirmOpen(true); }} sx={{ color: '#0ea5e9', '&:hover': { bgcolor: 'rgba(14, 165, 233, 0.1)' } }}>
                                        <IconRotateClockwise size={18} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title="Düzenle">
                                <IconButton size="small" onClick={() => openEditDrawer(campaign)} sx={{ color: '#3b82f6', '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.1)' } }}>
                                    <IconEdit size={18} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="CSV Yükle">
                                <IconButton size="small" onClick={() => { setSelectedCampaign(campaign); setCsvDialogOpen(true); }} sx={{ color: '#8b5cf6', '&:hover': { bgcolor: 'rgba(139, 92, 246, 0.1)' } }}>
                                    <IconUpload size={18} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                    
                    {/* Progress */}
                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontFamily: 'Arial' }}>İlerleme</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#334155', fontFamily: 'Arial' }}>
                                {campaign.statistics?.contactsDialed || 0} / {campaign.statistics?.totalContacts || 0}
                            </Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={progress}
                            sx={{ height: 6, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: status.color, borderRadius: 3 } }}
                        />
                    </Box>
                    
                    {/* Quick stats */}
                    <Box sx={{ display: 'flex', gap: 0, bgcolor: '#f8fafc', borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                        <Box sx={{ flex: 1, textAlign: 'center', py: 1.5, borderRight: '1px solid #e2e8f0' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#152b69', fontSize: '1.1rem', fontFamily: 'Arial' }}>
                                {campaign.statistics?.totalCalls || campaign.statistics?.contactsDialed || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', fontFamily: 'Arial', fontSize: '0.65rem' }}>Toplam Çağrı</Typography>
                        </Box>
                        <Box sx={{ flex: 1, textAlign: 'center', py: 1.5, borderRight: '1px solid #e2e8f0' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#22c55e', fontSize: '1.1rem', fontFamily: 'Arial' }}>
                                {campaign.statistics?.answeredCalls || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', fontFamily: 'Arial', fontSize: '0.65rem' }}>Cevaplanan</Typography>
                        </Box>
                        <Box sx={{ flex: 1, textAlign: 'center', py: 1.5 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#3b82f6', fontSize: '1.1rem', fontFamily: 'Arial' }}>
                                {(campaign.statistics?.totalCalls || campaign.statistics?.contactsDialed) ? Math.round(((campaign.statistics?.answeredCalls || 0) / (campaign.statistics?.totalCalls || campaign.statistics?.contactsDialed || 1)) * 100) : 0}%
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', fontFamily: 'Arial', fontSize: '0.65rem' }}>Başarı</Typography>
                        </Box>
                    </Box>
                </CardContent>
                
                {/* Expand toggle */}
                <Box onClick={() => toggleExpand(campaign._id)}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1,
                        borderTop: '1px solid #e2e8f0', cursor: 'pointer', transition: 'all 0.2s',
                        bgcolor: isExpanded ? '#f1f5f9' : 'transparent', '&:hover': { bgcolor: '#f1f5f9' }
                    }}>
                    <Typography variant="caption" sx={{ color: '#64748b', mr: 0.5, fontFamily: 'Arial', fontWeight: 500 }}>
                        {isExpanded ? 'Daha az göster' : 'Detaylar'}
                    </Typography>
                    {isExpanded ? <IconChevronUp size={16} color="#64748b" /> : <IconChevronDown size={16} color="#64748b" />}
                </Box>
                
                <Collapse in={isExpanded}>
                    <Box sx={{ p: 2.5, pt: 2, borderTop: '1px solid #e2e8f0', bgcolor: '#fafbfc' }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'Arial', fontWeight: 500, textTransform: 'uppercase', fontSize: '0.65rem' }}>Caller ID</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', fontFamily: 'Arial' }}>{campaign.callerIdNumber || '-'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'Arial', fontWeight: 500, textTransform: 'uppercase', fontSize: '0.65rem' }}>Maks. Eş Zamanlı</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', fontFamily: 'Arial' }}>{campaign.dialerSettings?.maxConcurrentCalls || 10}</Typography>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'Arial', fontWeight: 500, textTransform: 'uppercase', fontSize: '0.65rem' }}>Ring Timeout</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', fontFamily: 'Arial' }}>{campaign.dialerSettings?.ringTimeout || 25}s</Typography>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'Arial', fontWeight: 500, textTransform: 'uppercase', fontSize: '0.65rem' }}>Retry</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', fontFamily: 'Arial' }}>{campaign.dialerSettings?.retryAttempts || 3}x / {campaign.dialerSettings?.retryDelayMinutes || 30}dk</Typography>
                            </Grid>
                        </Grid>
                        <Box sx={{ display: 'flex', gap: 1, mt: 2.5 }}>
                            <Button size="small" variant="outlined" startIcon={<IconCopy size={16} />} onClick={() => handleClone(campaign)}
                                sx={{ borderColor: '#cbd5e1', color: '#475569', fontFamily: 'Arial', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: '#3b82f6', color: '#3b82f6', bgcolor: 'rgba(59, 130, 246, 0.04)' } }}>
                                Klonla
                            </Button>
                            <Button size="small" variant="outlined" color="error" startIcon={<IconTrash size={16} />}
                                onClick={() => { setSelectedCampaign(campaign); setDeleteConfirmOpen(true); }}
                                sx={{ textTransform: 'none', fontWeight: 600, fontFamily: 'Arial' }}>
                                Sil
                            </Button>
                        </Box>
                    </Box>
                </Collapse>
            </Card>
        );
    };
    
    // ─── Render Form ───
    const renderForm = () => (
        <Box>
            <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} 
                sx={{ mb: 3, borderBottom: 1, borderColor: '#e2e8f0',
                    '& .MuiTab-root': { fontFamily: 'Arial', textTransform: 'none', fontWeight: 600, color: '#64748b', '&.Mui-selected': { color: '#1e3a8a' } },
                    '& .MuiTabs-indicator': { bgcolor: '#1e3a8a' }
                }}>
                <Tab label="Genel" />
                <Tab label="Dialer Ayarları" />
                <Tab label="Çalışma Saatleri" />
            </Tabs>
            
            {tabValue === 0 && (
                <Grid container spacing={2.5}>
                    <Grid size={{ xs: 12 }}>
                        <TextField fullWidth label="Dialer Adı" value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })} required
                            sx={{ '& .MuiInputBase-root': { fontFamily: 'Arial' } }} />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField fullWidth label="Açıklama" value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })} multiline rows={2}
                            sx={{ '& .MuiInputBase-root': { fontFamily: 'Arial' } }} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth>
                            <InputLabel>Dialer Tipi</InputLabel>
                            <Select value={formData.type} label="Dialer Tipi"
                                onChange={(e) => setFormData({ ...formData, type: e.target.value as Campaign['type'] })}>
                                {Object.entries(dialerTypes).map(([key, { label, description }]) => (
                                    <MenuItem key={key} value={key}>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontFamily: 'Arial' }}>{label}</Typography>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontFamily: 'Arial' }}>{description}</Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth required>
                            <InputLabel>Kuyruk *</InputLabel>
                            <Select value={formData.queueId} label="Kuyruk *"
                                onChange={(e) => setFormData({ ...formData, queueId: e.target.value })}>
                                {queues.map(queue => (
                                    <MenuItem key={queue._id} value={queue._id}>
                                        {queue.name} ({queue.assignedAgentIds?.length || 0} agent)
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth label="Arayan Numara (Caller ID)" value={formData.callerIdNumber}
                            onChange={(e) => setFormData({ ...formData, callerIdNumber: e.target.value })}
                            sx={{ '& .MuiInputBase-root': { fontFamily: 'Arial' } }} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth label="Arayan İsim" value={formData.callerIdName}
                            onChange={(e) => setFormData({ ...formData, callerIdName: e.target.value })}
                            sx={{ '& .MuiInputBase-root': { fontFamily: 'Arial' } }} />
                    </Grid>
                </Grid>
            )}
            
            {tabValue === 1 && (
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" sx={{ color: '#475569', fontFamily: 'Arial', fontWeight: 500 }} gutterBottom>
                            Maks. Eş Zamanlı Çağrı: <strong>{formData.dialerSettings.maxConcurrentCalls}</strong>
                        </Typography>
                        <Slider value={formData.dialerSettings.maxConcurrentCalls}
                            onChange={(_, v) => setFormData({ ...formData, dialerSettings: { ...formData.dialerSettings, maxConcurrentCalls: v as number } })}
                            min={1} max={50} marks={[{ value: 1, label: '1' }, { value: 25, label: '25' }, { value: 50, label: '50' }]}
                            sx={{ color: '#1e3a8a' }} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" sx={{ color: '#475569', fontFamily: 'Arial', fontWeight: 500 }} gutterBottom>
                            Ring Timeout: <strong>{formData.dialerSettings.ringTimeout}s</strong>
                        </Typography>
                        <Slider value={formData.dialerSettings.ringTimeout}
                            onChange={(_, v) => setFormData({ ...formData, dialerSettings: { ...formData.dialerSettings, ringTimeout: v as number } })}
                            min={10} max={60} marks={[{ value: 10, label: '10s' }, { value: 30, label: '30s' }, { value: 60, label: '60s' }]}
                            sx={{ color: '#1e3a8a' }} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" sx={{ color: '#475569', fontFamily: 'Arial', fontWeight: 500 }} gutterBottom>
                            Retry Sayısı: <strong>{formData.dialerSettings.retryAttempts}</strong>
                        </Typography>
                        <Slider value={formData.dialerSettings.retryAttempts}
                            onChange={(_, v) => setFormData({ ...formData, dialerSettings: { ...formData.dialerSettings, retryAttempts: v as number } })}
                            min={0} max={10} marks sx={{ color: '#1e3a8a' }} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" sx={{ color: '#475569', fontFamily: 'Arial', fontWeight: 500 }} gutterBottom>
                            Retry Bekleme: <strong>{formData.dialerSettings.retryDelayMinutes} dk</strong>
                        </Typography>
                        <Slider value={formData.dialerSettings.retryDelayMinutes}
                            onChange={(_, v) => setFormData({ ...formData, dialerSettings: { ...formData.dialerSettings, retryDelayMinutes: v as number } })}
                            min={5} max={120} marks={[{ value: 5, label: '5dk' }, { value: 60, label: '1sa' }, { value: 120, label: '2sa' }]}
                            sx={{ color: '#1e3a8a' }} />
                    </Grid>
                </Grid>
            )}
            
            {tabValue === 2 && (
                <Box>
                    <FormControlLabel
                        control={
                            <Switch checked={formData.workingHours.enabled}
                                onChange={(e) => setFormData({ ...formData, workingHours: { ...formData.workingHours, enabled: e.target.checked } })}
                                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#1e3a8a' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#1e3a8a' } }} />
                        }
                        label={<Typography sx={{ fontFamily: 'Arial', fontWeight: 500, color: '#334155' }}>Çalışma saatlerini etkinleştir</Typography>}
                    />
                    {formData.workingHours.enabled && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ color: '#475569', fontFamily: 'Arial', fontWeight: 500, mb: 1 }}>Çalışma Günleri</Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.5 }}>
                                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                                    <Chip key={day} label={day.charAt(0).toUpperCase() + day.slice(1, 3)}
                                        onClick={() => {
                                            const days = formData.workingHours.days.includes(day)
                                                ? formData.workingHours.days.filter(d => d !== day) : [...formData.workingHours.days, day];
                                            setFormData({ ...formData, workingHours: { ...formData.workingHours, days } });
                                        }}
                                        sx={{ fontFamily: 'Arial', fontWeight: 600,
                                            ...(formData.workingHours.days.includes(day) 
                                                ? { bgcolor: '#1e3a8a', color: 'white', '&:hover': { bgcolor: '#152b69' } }
                                                : { bgcolor: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', '&:hover': { bgcolor: '#e2e8f0' } })
                                        }} />
                                ))}
                            </Box>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 6 }}>
                                    <TextField fullWidth label="Başlangıç Saati" type="time"
                                        value={formData.workingHours.timeSlots[0]?.startTime || '09:00'}
                                        onChange={(e) => setFormData({ ...formData, workingHours: { ...formData.workingHours, timeSlots: [{ ...formData.workingHours.timeSlots[0], startTime: e.target.value }] } })}
                                        slotProps={{ inputLabel: { shrink: true } }} />
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <TextField fullWidth label="Bitiş Saati" type="time"
                                        value={formData.workingHours.timeSlots[0]?.endTime || '18:00'}
                                        onChange={(e) => setFormData({ ...formData, workingHours: { ...formData.workingHours, timeSlots: [{ ...formData.workingHours.timeSlots[0], endTime: e.target.value }] } })}
                                        slotProps={{ inputLabel: { shrink: true } }} />
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
    
    // ─── Loading ───
    if (loading && campaigns.length === 0) {
        return (
            <Box sx={{ p: 3, fontFamily: 'Arial, Helvetica, sans-serif' }}>
                <Grid container spacing={3}>
                    {[1, 2, 3, 4].map(i => (
                        <Grid key={i} size={{ xs: 12, sm: 6, lg: 3 }}>
                            <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
                        </Grid>
                    ))}
                    {[1, 2, 3].map(i => (
                        <Grid key={i} size={{ xs: 12, md: 6, lg: 4 }}>
                            <Skeleton variant="rounded" height={280} sx={{ borderRadius: 3 }} />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }
    
    // ─── Main Render ───
    return (
        <Box sx={{ p: 3, fontFamily: 'Arial, Helvetica, sans-serif', '& .MuiTypography-root': { fontFamily: 'Arial, Helvetica, sans-serif' } }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                <Box sx={{
                    width: '100%', bgcolor: 'white', borderRadius: 3,
                    px: { xs: 2, sm: 4 }, py: { xs: 2, sm: 2.5 },
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)', border: '1px solid rgba(0,0,0,0.06)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' }, color: '#152b69 !important' }}>
                            Dialer Yönetimi
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' }, color: '#64748b' }}>
                            Dialer listelerini yönetin ve izleyin
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <Chip
                            icon={<Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                                {serviceStatus?.isRunning
                                    ? <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#22c55e', animation: `${pulse} 2s ease-in-out infinite` }} />
                                    : <IconPower size={14} />}
                            </Box>}
                            label={serviceStatus?.isRunning ? 'Servis Aktif' : 'Servis Kapalı'}
                            onClick={handleServiceToggle}
                            sx={{ fontWeight: 600, fontFamily: 'Arial', height: 36, cursor: 'pointer', '&:hover': { opacity: 0.85 },
                                ...(serviceStatus?.isRunning
                                    ? { bgcolor: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }
                                    : { bgcolor: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' })
                            }} />
                        <Button variant="contained" startIcon={<IconPlus size={18} />}
                            onClick={() => { resetForm(); setCreateDialogOpen(true); }}
                            sx={{ borderRadius: 2.5, px: 3, py: 1, bgcolor: '#1e3a8a', fontWeight: 600, textTransform: 'none', fontFamily: 'Arial',
                                boxShadow: '0 4px 12px rgba(30, 58, 138, 0.3)', '&:hover': { bgcolor: '#152b69' } }}>
                            Yeni Dialer
                        </Button>
                        <IconButton onClick={loadData}
                            sx={{ color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 2, '&:hover': { bgcolor: '#f1f5f9', color: '#1e3a8a' } }}>
                            <IconRefresh size={20} />
                        </IconButton>
                    </Box>
                </Box>
            </Box>
            
            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3, borderRadius: 2, fontFamily: 'Arial', border: '1px solid #fecaca' }}>
                    {error}
                </Alert>
            )}
            
            {/* Main Tabs */}
            <Box sx={{ mb: 3 }}>
                <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)}
                    sx={{
                        minHeight: 44,
                        '& .MuiTabs-indicator': { bgcolor: '#1e3a8a', height: 3, borderRadius: '3px 3px 0 0' },
                        '& .MuiTab-root': {
                            textTransform: 'none', fontWeight: 600, fontFamily: 'Arial', fontSize: '0.95rem',
                            color: '#64748b', minHeight: 44, px: 3,
                            '&.Mui-selected': { color: '#1e3a8a' }
                        }
                    }}>
                    <Tab icon={<IconSpeakerphone size={18} />} iconPosition="start" label="Dialerlar" />
                    <Tab icon={<IconDatabase size={18} />} iconPosition="start" label="Veri Havuzu" />
                </Tabs>
                <Divider sx={{ borderColor: '#e2e8f0' }} />
            </Box>
            
            {/* Tab 0: Campaigns */}
            {mainTab === 0 && (<>
            {/* Stats */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <StatCard title="Toplam Dialer" value={campaigns.length}
                        subtitle={`${runningCampaigns} aktif, ${pausedCampaigns} duraklatılmış`}
                        icon={<IconSpeakerphone size={22} color="white" />}
                        gradient="linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <StatCard title="Toplam Kişi" value={totalStats.totalContacts.toLocaleString()}
                        subtitle={`${totalStats.contactsDialed.toLocaleString()} arandı`}
                        icon={<IconUsers size={22} color="white" />}
                        gradient="linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <StatCard title="Toplam Çağrı" value={totalStats.totalCalls.toLocaleString()}
                        subtitle={`${totalStats.answeredCalls.toLocaleString()} cevaplandı`}
                        icon={<IconPhoneCall size={22} color="white" />}
                        gradient="linear-gradient(135deg, #22c55e 0%, #16a34a 100%)" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <StatCard title="Başarı Oranı"
                        value={`${totalStats.totalCalls ? Math.round((totalStats.answeredCalls / totalStats.totalCalls) * 100) : 0}%`}
                        subtitle="Cevaplama oranı"
                        icon={<IconTarget size={22} color="white" />}
                        gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)" />
                </Grid>
            </Grid>
            
            {/* Campaigns */}
            <Grid container spacing={3}>
                {campaigns.map(campaign => (
                    <Grid key={campaign._id} size={{ xs: 12, md: 6, lg: 4 }}>
                        <CampaignCard campaign={campaign} />
                    </Grid>
                ))}
                {campaigns.length === 0 && (
                    <Grid size={{ xs: 12 }}>
                        <Card sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '2px dashed #cbd5e1', boxShadow: 'none', bgcolor: '#fafbfc' }}>
                            <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2.5 }}>
                                <IconSpeakerphone size={36} color="#94a3b8" />
                            </Box>
                            <Typography variant="h6" sx={{ color: '#475569', mb: 1, fontWeight: 600 }}>Henüz dialer yok</Typography>
                            <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>İlk dialer'ınızı oluşturarak dialer'ı kullanmaya başlayın</Typography>
                            <Button variant="contained" startIcon={<IconPlus size={18} />}
                                onClick={() => { resetForm(); setCreateDialogOpen(true); }}
                                sx={{ bgcolor: '#1e3a8a', textTransform: 'none', fontWeight: 600, fontFamily: 'Arial', borderRadius: 2.5, px: 4,
                                    boxShadow: '0 4px 12px rgba(30, 58, 138, 0.3)', '&:hover': { bgcolor: '#152b69' } }}>
                                Dialer Oluştur
                            </Button>
                        </Card>
                    </Grid>
                )}
            </Grid>
            </>)}
            
            {/* Tab 1: Data Pool */}
            {mainTab === 1 && (
                <Box>
                    {/* Pool Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#152b69', fontFamily: 'Arial' }}>
                                Veri Havuzu
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#94a3b8', fontFamily: 'Arial' }}>
                                Toplu yüklenen veriler burada saklanır ve dialerlara atanabilir
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Button variant="contained" startIcon={<IconUpload size={18} />}
                                onClick={() => setPoolDialogOpen(true)}
                                sx={{ borderRadius: 2.5, px: 3, py: 1, bgcolor: '#1e3a8a', fontWeight: 600, textTransform: 'none', fontFamily: 'Arial',
                                    boxShadow: '0 4px 12px rgba(30, 58, 138, 0.3)', '&:hover': { bgcolor: '#152b69' } }}>
                                CSV Yükle
                            </Button>
                            <IconButton onClick={loadPoolLists}
                                sx={{ color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 2, '&:hover': { bgcolor: '#f1f5f9', color: '#1e3a8a' } }}>
                                <IconRefresh size={20} />
                            </IconButton>
                        </Box>
                    </Box>
                    
                    {poolLoading ? (
                        <Grid container spacing={3}>
                            {[1, 2, 3].map(i => (
                                <Grid key={i} size={{ xs: 12, md: 6, lg: 4 }}>
                                    <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} />
                                </Grid>
                            ))}
                        </Grid>
                    ) : poolLists.length === 0 ? (
                        <Card sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '2px dashed #cbd5e1', boxShadow: 'none', bgcolor: '#fafbfc' }}>
                            <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2.5 }}>
                                <IconDatabase size={36} color="#94a3b8" />
                            </Box>
                            <Typography variant="h6" sx={{ color: '#475569', mb: 1, fontWeight: 600, fontFamily: 'Arial' }}>Havuzda veri yok</Typography>
                            <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3, fontFamily: 'Arial' }}>CSV dosyası yükleyerek veri havuzu oluşturun</Typography>
                            <Button variant="contained" startIcon={<IconUpload size={18} />}
                                onClick={() => setPoolDialogOpen(true)}
                                sx={{ bgcolor: '#1e3a8a', textTransform: 'none', fontWeight: 600, fontFamily: 'Arial', borderRadius: 2.5, px: 4,
                                    boxShadow: '0 4px 12px rgba(30, 58, 138, 0.3)', '&:hover': { bgcolor: '#152b69' } }}>
                                CSV Yükle
                            </Button>
                        </Card>
                    ) : (
                        <Grid container spacing={3}>
                            {poolLists.map(list => (
                                <Grid key={list._id} size={{ xs: 12, md: 6, lg: 4 }}>
                                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                        transition: 'all 0.2s', '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.1)', transform: 'translateY(-2px)' } }}>
                                        <CardContent sx={{ p: 3 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Box sx={{ width: 42, height: 42, borderRadius: 2, 
                                                        background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <IconFileSpreadsheet size={22} color="white" />
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#152b69', fontFamily: 'Arial', lineHeight: 1.3 }}>
                                                            {list.name}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'Arial' }}>
                                                            {list.importInfo?.fileName || 'CSV'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Chip size="small" label={list.status === 'active' ? 'Aktif' : list.status === 'completed' ? 'Tamamlandı' : 'Pasif'}
                                                    sx={{ fontWeight: 600, fontFamily: 'Arial', fontSize: '0.7rem',
                                                        bgcolor: list.status === 'active' ? '#dcfce7' : '#f1f5f9',
                                                        color: list.status === 'active' ? '#166534' : '#64748b' }} />
                                            </Box>
                                            
                                            {/* Stats Row */}
                                            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                                <Box sx={{ flex: 1, minWidth: 60, bgcolor: '#f8fafc', borderRadius: 2, p: 1, textAlign: 'center' }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e3a8a', fontFamily: 'Arial' }}>
                                                        {(list.statistics?.totalContacts || 0).toLocaleString()}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'Arial', fontSize: '0.65rem' }}>Toplam</Typography>
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 60, bgcolor: '#fffbeb', borderRadius: 2, p: 1, textAlign: 'center' }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#d97706', fontFamily: 'Arial' }}>
                                                        {(list.statistics?.pendingContacts || 0).toLocaleString()}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'Arial', fontSize: '0.65rem' }}>Bekleyen</Typography>
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 60, bgcolor: '#f0fdf4', borderRadius: 2, p: 1, textAlign: 'center' }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#16a34a', fontFamily: 'Arial' }}>
                                                        {(list.statistics?.completedContacts || 0).toLocaleString()}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'Arial', fontSize: '0.65rem' }}>Tamamlanan</Typography>
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 60, bgcolor: '#fef2f2', borderRadius: 2, p: 1, textAlign: 'center' }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#dc2626', fontFamily: 'Arial' }}>
                                                        {(list.statistics?.failedContacts || 0).toLocaleString()}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'Arial', fontSize: '0.65rem' }}>Başarısız</Typography>
                                                </Box>
                                            </Box>
                                            
                                            {/* Upload Date */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                                                <IconCalendar size={14} color="#94a3b8" />
                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'Arial' }}>
                                                    {new Date(list.createdAt).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                </Typography>
                                            </Box>
                                            
                                            {/* Actions */}
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button fullWidth variant="contained" size="small" startIcon={<IconArrowRight size={16} />}
                                                    onClick={() => { setAssignListId(list._id); setAssignDialogOpen(true); }}
                                                    sx={{ bgcolor: '#1e3a8a', textTransform: 'none', fontWeight: 600, fontFamily: 'Arial', borderRadius: 2,
                                                        fontSize: '0.8rem', py: 0.8,
                                                        boxShadow: '0 2px 8px rgba(30, 58, 138, 0.2)', '&:hover': { bgcolor: '#152b69' } }}>
                                                    Dialer'a Ata
                                                </Button>
                                                <Tooltip title="Rapor İndir">
                                                    <IconButton size="small" onClick={() => handleExportPoolList(list._id, list.name)}
                                                        sx={{ color: '#0ea5e9', border: '1px solid #bae6fd', borderRadius: 1.5,
                                                            '&:hover': { bgcolor: '#e0f2fe', borderColor: '#0ea5e9' } }}>
                                                        <IconDownload size={16} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Sil">
                                                    <IconButton size="small" onClick={() => handleDeletePoolList(list._id)}
                                                        sx={{ color: '#ef4444', border: '1px solid #fecaca', borderRadius: 1.5,
                                                            '&:hover': { bgcolor: '#fef2f2', borderColor: '#ef4444' } }}>
                                                        <IconTrash size={16} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>
            )}
            
            {/* Create Dialog */}
            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth
                PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)' } }}>
                <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #e2e8f0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: 2, background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <IconPlus size={20} color="white" />
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#152b69' }}>Yeni Dialer</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>{renderForm()}</DialogContent>
                <DialogActions sx={{ p: 2.5, pt: 0, borderTop: '1px solid #e2e8f0' }}>
                    <Button onClick={() => setCreateDialogOpen(false)} sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600, fontFamily: 'Arial' }}>İptal</Button>
                    <Button variant="contained" onClick={handleCreate} disabled={!formData.name || !formData.queueId}
                        sx={{ bgcolor: '#1e3a8a', textTransform: 'none', fontWeight: 600, fontFamily: 'Arial', borderRadius: 2, px: 4, '&:hover': { bgcolor: '#152b69' } }}>
                        Oluştur
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Edit Drawer */}
            <Drawer anchor="right" open={editDrawerOpen} onClose={() => setEditDrawerOpen(false)}
                PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, p: 3, boxShadow: '-8px 0 30px rgba(0, 0, 0, 0.1)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: 2, background: 'linear-gradient(135deg, #3b82f6 0%, #1e3a8a 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <IconEdit size={20} color="white" />
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#152b69' }}>Dialer'ı Düzenle</Typography>
                    </Box>
                    <IconButton onClick={() => setEditDrawerOpen(false)} sx={{ color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}>
                        <IconX size={20} />
                    </IconButton>
                </Box>
                <Divider sx={{ mb: 3, borderColor: '#e2e8f0' }} />
                {renderForm()}
                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button fullWidth variant="outlined" onClick={() => setEditDrawerOpen(false)}
                        sx={{ borderColor: '#cbd5e1', color: '#64748b', textTransform: 'none', fontWeight: 600, fontFamily: 'Arial', borderRadius: 2, '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' } }}>
                        İptal
                    </Button>
                    <Button fullWidth variant="contained" onClick={handleUpdate}
                        sx={{ bgcolor: '#1e3a8a', textTransform: 'none', fontWeight: 600, fontFamily: 'Arial', borderRadius: 2, boxShadow: '0 4px 12px rgba(30, 58, 138, 0.3)', '&:hover': { bgcolor: '#152b69' } }}>
                        Kaydet
                    </Button>
                </Box>
            </Drawer>
            
            {/* Delete Confirm */}
            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#152b69' }}>
                    <IconAlertCircle size={24} color="#ef4444" /> Dialer'ı Sil
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: 'Arial', color: '#475569' }}>
                        <strong>{selectedCampaign?.name}</strong> dialer'ını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDeleteConfirmOpen(false)} sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600, fontFamily: 'Arial' }}>İptal</Button>
                    <Button variant="contained" onClick={handleDelete}
                        sx={{ bgcolor: '#ef4444', textTransform: 'none', fontWeight: 600, fontFamily: 'Arial', borderRadius: 2, '&:hover': { bgcolor: '#dc2626' } }}>
                        Sil
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Restart Confirm */}
            <Dialog open={restartConfirmOpen} onClose={() => setRestartConfirmOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#152b69' }}>
                    <IconAlertCircle size={24} color="#0ea5e9" /> Baştan Başlat
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: 'Arial', color: '#475569' }}>
                        <strong>{selectedCampaign?.name}</strong> dialer'ını sıfırlayıp baştan başlatmak istediğinize emin misiniz?
                    </Typography>
                    <Typography sx={{ fontFamily: 'Arial', color: '#ef4444', fontSize: '0.85rem', mt: 1 }}>
                        ⚠️ Tüm kişilerin arama geçmişi silinecek ve herkes tekrar aranacak.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setRestartConfirmOpen(false)} sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600, fontFamily: 'Arial' }}>İptal</Button>
                    <Button variant="contained" onClick={handleRestart}
                        sx={{ bgcolor: '#0ea5e9', textTransform: 'none', fontWeight: 600, fontFamily: 'Arial', borderRadius: 2, '&:hover': { bgcolor: '#0284c7' } }}>
                        Sıfırla ve Başlat
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* CSV Dialog */}
            <Dialog open={csvDialogOpen} onClose={() => setCsvDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#152b69', borderBottom: '1px solid #e2e8f0' }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 2, background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IconUpload size={18} color="white" />
                    </Box>
                    CSV Yükle - {selectedCampaign?.name}
                </DialogTitle>
                <DialogContent>
                    <TextField fullWidth label="Liste Adı" value={csvListName}
                        onChange={(e) => setCsvListName(e.target.value)}
                        sx={{ mt: 2.5, mb: 2, '& .MuiInputBase-root': { fontFamily: 'Arial' } }} />
                    <Button variant="outlined" component="label" fullWidth startIcon={<IconUpload size={18} />}
                        sx={{ py: 2, borderColor: csvFile ? '#22c55e' : '#cbd5e1', color: csvFile ? '#166534' : '#64748b',
                            bgcolor: csvFile ? '#f0fdf4' : 'transparent', textTransform: 'none', fontWeight: 600, fontFamily: 'Arial',
                            borderRadius: 2, borderStyle: 'dashed', '&:hover': { borderColor: '#3b82f6', bgcolor: '#f8fafc' } }}>
                        {csvFile ? `✓ ${csvFile.name}` : 'CSV Dosyası Seç'}
                        <input type="file" accept=".csv" hidden
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) { setCsvFile(file); if (!csvListName) setCsvListName(file.name.replace('.csv', '')); }
                            }} />
                    </Button>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
                    <Button onClick={() => { setCsvDialogOpen(false); setCsvFile(null); }}
                        sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600, fontFamily: 'Arial' }}>İptal</Button>
                    <Button variant="contained" onClick={handleCsvUpload} disabled={!csvFile || csvUploading}
                        sx={{ bgcolor: '#1e3a8a', textTransform: 'none', fontWeight: 600, fontFamily: 'Arial', borderRadius: 2, px: 4, '&:hover': { bgcolor: '#152b69' } }}>
                        {csvUploading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Yükle'}
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Pool CSV Upload Dialog */}
            <Dialog open={poolDialogOpen} onClose={() => setPoolDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#152b69', borderBottom: '1px solid #e2e8f0' }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 2, background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IconDatabase size={18} color="white" />
                    </Box>
                    Veri Havuzuna CSV Yükle
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: '#94a3b8', fontFamily: 'Arial', mt: 1, mb: 2 }}>
                        Yüklenen veriler havuzda saklanır ve daha sonra istediğiniz dialer'a atanabilir.
                    </Typography>
                    <TextField fullWidth label="Liste Adı" value={poolListName}
                        onChange={(e) => setPoolListName(e.target.value)}
                        sx={{ mb: 2, '& .MuiInputBase-root': { fontFamily: 'Arial' } }} />
                    <Button variant="outlined" component="label" fullWidth startIcon={<IconUpload size={18} />}
                        sx={{ py: 2, borderColor: poolCsvFile ? '#22c55e' : '#cbd5e1', color: poolCsvFile ? '#166534' : '#64748b',
                            bgcolor: poolCsvFile ? '#f0fdf4' : 'transparent', textTransform: 'none', fontWeight: 600, fontFamily: 'Arial',
                            borderRadius: 2, borderStyle: 'dashed', '&:hover': { borderColor: '#3b82f6', bgcolor: '#f8fafc' } }}>
                        {poolCsvFile ? `✓ ${poolCsvFile.name}` : 'CSV Dosyası Seç'}
                        <input type="file" accept=".csv" hidden
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) { setPoolCsvFile(file); if (!poolListName) setPoolListName(file.name.replace('.csv', '')); }
                            }} />
                    </Button>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
                    <Button onClick={() => { setPoolDialogOpen(false); setPoolCsvFile(null); setPoolListName(''); }}
                        sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600, fontFamily: 'Arial' }}>İptal</Button>
                    <Button variant="contained" onClick={handlePoolCsvUpload} disabled={!poolCsvFile || poolUploading}
                        sx={{ bgcolor: '#1e3a8a', textTransform: 'none', fontWeight: 600, fontFamily: 'Arial', borderRadius: 2, px: 4, '&:hover': { bgcolor: '#152b69' } }}>
                        {poolUploading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Havuza Yükle'}
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Assign to Campaign Dialog */}
            <Dialog open={assignDialogOpen} onClose={() => { setAssignDialogOpen(false); setAssignCampaignId(''); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#152b69', borderBottom: '1px solid #e2e8f0' }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 2, background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IconArrowRight size={18} color="white" />
                    </Box>
                    Dialer'a Ata
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: '#94a3b8', fontFamily: 'Arial', mt: 1, mb: 2 }}>
                        Bu listedeki kişiler seçilen dialer'a kopyalanacaktır. Havuzdaki orijinal veriler korunur.
                    </Typography>
                    <FormControl fullWidth sx={{ mt: 1 }}>
                        <InputLabel sx={{ fontFamily: 'Arial' }}>Dialer Seçin</InputLabel>
                        <Select value={assignCampaignId} label="Dialer Seçin"
                            onChange={(e) => setAssignCampaignId(e.target.value as string)}
                            sx={{ fontFamily: 'Arial' }}>
                            {campaigns.map(c => (
                                <MenuItem key={c._id} value={c._id} sx={{ fontFamily: 'Arial' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <IconSpeakerphone size={16} color="#64748b" />
                                        {c.name}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
                    <Button onClick={() => { setAssignDialogOpen(false); setAssignCampaignId(''); }}
                        sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600, fontFamily: 'Arial' }}>İptal</Button>
                    <Button variant="contained" onClick={handleAssignToCampaign} disabled={!assignCampaignId || assigning}
                        sx={{ bgcolor: '#1e3a8a', textTransform: 'none', fontWeight: 600, fontFamily: 'Arial', borderRadius: 2, px: 4, '&:hover': { bgcolor: '#152b69' } }}>
                        {assigning ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Ata'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
